import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Settings, Volume2, VolumeX, Mic, MicOff } from "lucide-react";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { soundPlayer } from "../../utils/sound";
import { cn } from "../../utils/cn";

interface WorkoutSettingsProps {
  soundEnabled: boolean;
  toggleSound: () => void;
  voiceEnabled: boolean;
  toggleVoice: () => void;
  volume: number;
  setVolume: (volume: number) => void;
  onReset?: () => void;
  onExit?: () => void;
}

export function WorkoutSettings({
  soundEnabled,
  toggleSound,
  voiceEnabled,
  toggleVoice,
  volume,
  setVolume,
  onReset,
  onExit,
}: WorkoutSettingsProps) {
  const handleTestSound = () => {
    if (soundEnabled) {
      soundPlayer.setComplete();
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="h-8 w-8">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Workout Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="audio">Audio</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 pt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="voice-commands">Voice Commands</Label>
                <div className="flex items-center gap-2">
                  {voiceEnabled ? (
                    <Mic className="h-4 w-4" />
                  ) : (
                    <MicOff className="h-4 w-4" />
                  )}
                  <Switch
                    id="voice-commands"
                    checked={voiceEnabled}
                    onCheckedChange={toggleVoice}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <h4 className="text-sm font-medium">Workout Controls</h4>
                <div className="grid grid-cols-2 gap-2">
                  {onReset && (
                    <Button variant="outline" onClick={onReset}>
                      Reset Workout
                    </Button>
                  )}
                  {onExit && (
                    <Button variant="destructive" onClick={onExit}>
                      Exit Workout
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="audio" className="space-y-4 pt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="sound-enabled">Sound Effects</Label>
                <div className="flex items-center gap-2">
                  {soundEnabled ? (
                    <Volume2 className="h-4 w-4" />
                  ) : (
                    <VolumeX className="h-4 w-4" />
                  )}
                  <Switch
                    id="sound-enabled"
                    checked={soundEnabled}
                    onCheckedChange={toggleSound}
                  />
                </div>
              </div>

              {soundEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="volume">Volume</Label>
                  <div className="flex items-center gap-2">
                    <VolumeX className="h-4 w-4" />
                    <Slider
                      id="volume"
                      value={[volume]}
                      min={0}
                      max={1}
                      step={0.1}
                      onValueChange={(values) => setVolume(values[0])}
                      className="flex-1"
                    />
                    <Volume2 className="h-4 w-4" />
                  </div>

                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestSound}
                    >
                      Test Sound
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
